import * as React from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import CustomCoursesContext from 'bundles/enterprise-collections/context/CustomCoursesContext';
import PrivateCoursesQuery from 'bundles/enterprise-collections/queries/PrivateCourses.graphql';
import ProgramCurriculumCollectionsQuery from 'bundles/enterprise-collections/queries/ProgramCurriculumCollections.graphql';
import type {
  PrivateCourseFieldsFragment,
  PrivateCoursesQuery as PrivateCoursesQueryData,
  PrivateCoursesQueryVariables,
} from 'bundles/enterprise-collections/queries/__generated__/PrivateCourses';
import type {
  ProgramCurriculumCollectionsQuery as ProgramCurriculumCollectionsQueryData,
  ProgramCurriculumCollectionsQueryVariables,
} from 'bundles/enterprise-collections/queries/__generated__/ProgramCurriculumCollections';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

type Props = {
  children: React.ReactNode;
};

const BATCH_SIZE = 50;
const PRIVATE_COURSES_BATCH_SIZE = 100;

/**
 * Hook to fetch private courses in batches of 100.
 * It takes an array of course IDs and fetches the corresponding private courses
 * in batches to avoid overwhelming the server with too many IDs at once.
 * The hook maintains its own state for tracking the current batch and accumulated courses.
 */
const usePrivateCoursesQuery = (courseIds: string[]) => {
  const [currentBatchIndex, setCurrentBatchIndex] = React.useState(0);
  const batches = React.useMemo(() => {
    const result: string[][] = [];
    for (let i = 0; i < courseIds.length; i += PRIVATE_COURSES_BATCH_SIZE) {
      result.push(courseIds.slice(i, i + PRIVATE_COURSES_BATCH_SIZE));
    }
    return result;
  }, [courseIds]);

  const { data, loading } = useQuery<PrivateCoursesQueryData, PrivateCoursesQueryVariables>(PrivateCoursesQuery, {
    variables: {
      courseIds: batches[currentBatchIndex] || [],
    },
    skip: !batches[currentBatchIndex]?.length,
    context: { clientName: 'gatewayGql' },
  });

  const [allPrivateCourses, setAllPrivateCourses] = React.useState<PrivateCourseFieldsFragment[]>([]);

  React.useEffect(() => {
    if (data?.EntUserEducator?.queryPrivateCoursesByCourseIds) {
      setAllPrivateCourses((prev) => [...prev, ...(data.EntUserEducator?.queryPrivateCoursesByCourseIds || [])]);
    }
  }, [data]);

  React.useEffect(() => {
    if (!loading && currentBatchIndex < batches.length - 1) {
      setCurrentBatchIndex((prev) => prev + 1);
    }
  }, [loading, currentBatchIndex, batches.length]);

  return {
    privateCourses: allPrivateCourses,
  };
};

/**
 * Provider component that fetches and manages curriculum collections and private courses for a program.
 * It handles pagination of curriculum collections and batches the fetching of private courses.
 * The provider exposes:
 * - privateCourses: Array of private courses associated with the program
 * - totalCollections: Total number of curriculum collections in the program
 */
const CustomCoursesProvider = ({ children }: Props) => {
  const program = useProgram();
  const programId = program?.id;

  const [allCourseIds, setAllCourseIds] = React.useState<string[]>([]);
  const [nextStart, setNextStart] = React.useState<string | undefined>(undefined);

  const { data, fetchMore } = useQuery<
    ProgramCurriculumCollectionsQueryData,
    ProgramCurriculumCollectionsQueryVariables
  >(ProgramCurriculumCollectionsQuery, {
    skip: !programId,
    variables: {
      programId: programId || '',
      limit: BATCH_SIZE,
    },
    context: { clientName: 'gatewayGql' },
  });

  const courseIds = React.useMemo(() => {
    const initialCourseIds =
      data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements
        .filter((track): track is NonNullable<typeof track> => track !== null)
        .flatMap((track) => track.courseIds)
        .filter((id): id is string => id !== null) || [];

    return [...initialCourseIds, ...allCourseIds];
  }, [data, allCourseIds]);

  const { privateCourses } = usePrivateCoursesQuery(courseIds);

  React.useEffect(() => {
    const newNextStart =
      data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.paging?.next;
    if (newNextStart) {
      setNextStart(newNextStart);
    }
  }, [data]);

  React.useEffect(() => {
    const fetchAllCourseIds = async () => {
      if (!nextStart) return;

      try {
        const result = await fetchMore({
          variables: {
            start: nextStart,
            limit: BATCH_SIZE,
          },
        });

        const newCourseIds =
          result.data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements
            .filter((track): track is NonNullable<typeof track> => track !== null)
            .flatMap((track) => track.courseIds)
            .filter((id): id is string => id !== null) || [];

        setAllCourseIds((prev) => [...prev, ...newCourseIds]);
        const newNextStart =
          result.data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.paging?.next;
        setNextStart(newNextStart || undefined);
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    fetchAllCourseIds();
  }, [nextStart, fetchMore]);

  const totalCollections = React.useMemo(() => {
    return data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.paging?.total || 0;
  }, [data]);

  return (
    <CustomCoursesContext.Provider
      value={{
        privateCourses,
        totalCollections,
      }}
    >
      {children}
    </CustomCoursesContext.Provider>
  );
};

export default CustomCoursesProvider;
